import React, { useEffect, useState } from 'react';
import { Grid, CircularProgress, Alert } from '@mui/material';
import CategoryChart from '../category/CategoryChart';
import CategoryTable from '../category/CategoryTable';
import CategoryLegend from '../category/CategoryLegend';
import CategoryModal from '../category/CategoryModal';
import walletApi from '../../api/wallet';
import { CHART_COLORS } from '../../constants/layout';
import {
  SCALE_MODES,
  toPercentage,
  detectOutliers,
  extractCategoryValues,
  getScaleMaxWithoutOutliers
} from '../../utils/chartScaleUtils';

const CashInCategoryDashboard = ({ currency, numRecords = 12, chartType = 'bar' }) => {
  const [loading, setLoading] = useState(true);
  const [categoryData, setCategoryData] = useState([]);
  const [selectedYear, setSelectedYear] = useState('All');
  const [hiddenCategories, setHiddenCategories] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [error, setError] = useState(null);
  const [requestedMonths, setRequestedMonths] = useState(numRecords);
  const [currentDataMonths, setCurrentDataMonths] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(numRecords);
  const [scaleMode, setScaleMode] = useState(() => {
    return localStorage.getItem('cashInScaleMode') || SCALE_MODES.ABSOLUTE;
  });
  const [outlierInfo, setOutlierInfo] = useState({ outliers: [], outlierCategories: [] });

  useEffect(() => {
    fetchCategoryData();
  }, [currency]);

  useEffect(() => {
    setSelectedPeriod(numRecords);
    setRequestedMonths(numRecords);
    if (numRecords > currentDataMonths && currentDataMonths > 0) {
      setLoadingMore(true);
      fetchCategoryData(numRecords);
    }
  }, [numRecords]);

  const fetchCategoryData = async (months = requestedMonths) => {
    try {
      const response = await walletApi.get(`/cashin/consolidate/category?months=${months}`)
      if (response.status === 200) {
        setCategoryData(processCategoryData(response.data));
        setCurrentDataMonths(months);
      } else if (response.status === 204) {
        setCategoryData([]);
        setError('Nenhum dado de receita consolidado por categoria encontrado.');
      }
      setLoading(false);
      setLoadingMore(false);
    } catch (err) {
      setError('Não foi possível carregar os dados.');
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const processCategoryData = (data) => {
    const processedData = [];
    data.forEach((item) => {
      item.Categories.forEach((category) => {
        let existingCategory = processedData.find((c) => c.category === category.Category);
        if (!existingCategory) {
          existingCategory = {
            category: category.Category,
            total: 0,
            monthlyBreakdown: {},
          };
          processedData.push(existingCategory);
        }

        existingCategory.total += parseFloat(category.Currencies[currency]) || 0;

        const [year, month] = item.YearMonth.split('-');
        if (!existingCategory.monthlyBreakdown[year]) {
          existingCategory.monthlyBreakdown[year] = Array(12).fill(0);
        }

        const monthIndex = parseInt(month, 10) - 1;
        existingCategory.monthlyBreakdown[year][monthIndex] = parseFloat(category.Currencies[currency]) || 0;
      });
    });

    return processedData;
  };

  const toggleCategoryVisibility = (category) => {
    setHiddenCategories((prevHiddenCategories) =>
      prevHiddenCategories.includes(category)
        ? prevHiddenCategories.filter((item) => item !== category)
        : [...prevHiddenCategories, category]
    );
  };

  const handleExpandCategory = (category) => {
    setExpandedCategory(category);
  };

  const handleCloseModal = () => {
    setExpandedCategory(null);
  };

  const handleYearChange = (_, newValue) => {
    setSelectedYear(newValue || 'All');
  };

  const handleRequestMoreData = (numRecords) => {
    if (numRecords > currentDataMonths) {
      setRequestedMonths(numRecords);
      setLoadingMore(true);
      fetchCategoryData(numRecords);
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
  };

  const handleScaleModeChange = (_, newMode) => {
    if (newMode !== null) {
      setScaleMode(newMode);
      localStorage.setItem('cashInScaleMode', newMode);
    }
  };

  const processChartData = () => {
    const chartData = [];
    const monthNames = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

    if (selectedYear === 'All') {
      const recentMonthsLimit = Math.max(12, requestedMonths);
      const currentDate = new Date();
      const recentMonths = [];

      for (let i = 0; i < recentMonthsLimit; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        recentMonths.unshift({ month: date.getMonth(), year: date.getFullYear() });
      }

      categoryData.forEach(category => {
        recentMonths.forEach(({ month, year }) => {
          const monthLabel = `${monthNames[month]}-${year}`;
          let existingEntry = chartData.find(entry => entry.month === monthLabel);

          if (!existingEntry) {
            existingEntry = { month: monthLabel };
            chartData.push(existingEntry);
          }

          const value = category.monthlyBreakdown[year] && category.monthlyBreakdown[year][month];
          const validValue = value !== null && value !== undefined ? value : 0;

          existingEntry[category.category] = (existingEntry[category.category] || 0) +
            (parseFloat(validValue.toString().replace(/[^\d.-]/g, '')) || 0);
        });
      });

    } else {
      categoryData.forEach(category => {
        const year = selectedYear;

        if (category.monthlyBreakdown[year]) {
          category.monthlyBreakdown[year].forEach((value, index) => {
            const monthLabel = `${monthNames[index]}-${year}`;
            let existingEntry = chartData.find(entry => entry.month === monthLabel);

            if (!existingEntry) {
              existingEntry = { month: monthLabel };
              chartData.push(existingEntry);
            }

            const validValue = value !== null && value !== undefined ? value : 0;

            existingEntry[category.category] = (existingEntry[category.category] || 0) +
              (parseFloat(validValue.toString().replace(/[^\d.-]/g, '')) || 0);
          });
        }
      });
    }

    return chartData;
  };

  const colors = CHART_COLORS;

  const rawChartData = processChartData();
  const categoryNames = categoryData
    .filter(cat => !hiddenCategories.includes(cat.category))
    .map(cat => cat.category);

  useEffect(() => {
    if (rawChartData.length > 0 && categoryNames.length > 0) {
      const { values, categories } = extractCategoryValues(rawChartData, categoryNames);
      const { outliers, outlierCategories } = detectOutliers(values, categories);
      setOutlierInfo({ outliers, outlierCategories });
    }
  }, [categoryData, hiddenCategories, selectedYear, selectedPeriod]);

  const getProcessedChartData = () => {
    if (scaleMode === SCALE_MODES.PERCENTAGE) {
      return toPercentage(rawChartData, categoryNames);
    }
    return rawChartData;
  };

  const getScaleMax = () => {
    if (scaleMode !== SCALE_MODES.NORMALIZED) return undefined;
    const { values } = extractCategoryValues(rawChartData, categoryNames);
    return getScaleMaxWithoutOutliers(values);
  };

  const calculateTotalForYear = (monthlyValues) =>
    monthlyValues.reduce((sum, value) => sum + (parseFloat(value?.toString().replace(/[^\d.-]/g, '')) || 0), 0);

  const years = Array.from(new Set(categoryData.flatMap(category => Object.keys(category.monthlyBreakdown).sort((a, b) => parseInt(b) - parseInt(a)))));

  const buildFilteredCategoryData = () => {
    if (selectedYear === 'All') {
      const currentDate = new Date();
      const recentMonths = [];

      for (let i = 0; i < selectedPeriod; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        recentMonths.push({ month: date.getMonth(), year: date.getFullYear() });
      }

      const rows = categoryData.flatMap((category) => {
        const yearsInPeriod = [...new Set(recentMonths.map(m => m.year.toString()))];

        return yearsInPeriod.map((year) => {
          const monthsForYear = recentMonths
            .filter(m => m.year.toString() === year)
            .map(m => m.month);

          const monthlyValues = Array(12).fill(0);
          const breakdown = category.monthlyBreakdown[year] || [];

          monthsForYear.forEach(monthIdx => {
            monthlyValues[monthIdx] = breakdown[monthIdx] || 0;
          });

          const total = monthsForYear.reduce((sum, monthIdx) => {
            return sum + (parseFloat(breakdown[monthIdx]?.toString().replace(/[^\d.-]/g, '')) || 0);
          }, 0);

          return {
            ...category,
            total,
            currentYear: year,
            monthlyValues,
            rowKey: `${category.category}-${year}`,
          };
        });
      }).filter((row) => row.total > 0);

      return rows.sort((a, b) => {
        const yearDiff = parseInt(b.currentYear) - parseInt(a.currentYear);
        if (yearDiff !== 0) return yearDiff;
        return b.total - a.total;
      });
    }

    return categoryData
      .map((category) => {
        const monthlyValues = category.monthlyBreakdown[selectedYear] || [];
        const total = calculateTotalForYear(monthlyValues);
        return {
          ...category,
          total,
          currentYear: selectedYear,
          monthlyValues,
          rowKey: `${category.category}-${selectedYear}`,
        };
      })
      .filter((row) => row.total > 0)
      .sort((a, b) => b.total - a.total);
  };

  const filteredCategoryData = buildFilteredCategoryData();


  if (loading) {
    return <CircularProgress />;
  }

  if (error) {
    return (
      <Grid container spacing={2} justifyContent="center" sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Alert severity="error">{error}</Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12}>
          <CategoryChart
            chartData={getProcessedChartData()}
            categoryData={categoryData}
            hiddenCategories={hiddenCategories}
            colors={colors}
            onRequestMoreData={handleRequestMoreData}
            onPeriodChange={handlePeriodChange}
            loading={loadingMore}
            selectedYear={selectedYear}
            years={years}
            onYearChange={handleYearChange}
            chartType={chartType}
            numRecords={numRecords}
            scaleMode={scaleMode}
            scaleMax={getScaleMax()}
            onScaleModeChange={handleScaleModeChange}
            outlierInfo={outlierInfo}
          />
        </Grid>
        <Grid item xs={12} sx={{ textAlign: 'left',  }}>
          <CategoryLegend
            categoryData={categoryData}
            hiddenCategories={hiddenCategories}
            toggleCategoryVisibility={toggleCategoryVisibility}
            colors={colors}
            setHiddenCategories={setHiddenCategories}

          />
        </Grid>

        <Grid item xs={12}>
          <CategoryTable
            filteredCategoryData={filteredCategoryData}
            currency={currency}
            handleExpandCategory={handleExpandCategory}
            expandedCategory={expandedCategory}
          />
        </Grid>
      </Grid>

      {expandedCategory && (
        <CategoryModal
          expandedCategory={expandedCategory}
          handleCloseModal={handleCloseModal}
          calculateTotalForYear={calculateTotalForYear}
          currency={currency}
        />
      )}
    </>
  );
};

export default CashInCategoryDashboard;
